/**
 * Previewing a Windows system voice, and stopping speech on every engine.
 *
 * Split from `denker-agent-voice` because these only ever run on Windows, and
 * because the preview has to wait for a real terminal event rather than
 * returning when the request is queued — which is a good chunk of machinery
 * that the Kokoro and macOS paths have no use for.
 */
import { isTauri } from "@/lib/is-tauri";
import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";

import { voicePreviewLine } from "./voice-preview-line";
import {
  claimPreviewRequest,
  isCurrentPreviewRequest,
} from "./voice-preview-session";

/** How long to wait for a preview's terminal event before giving up on it.
 *  The preview line takes about three seconds to speak; this is a backstop
 *  against a dropped event leaving the picker's play icon lit forever, not a
 *  expected timeout. */
const PREVIEW_TERMINAL_TIMEOUT_MS = 15_000;

/** Arm a one-shot wait for an utterance's terminal state.
 *
 *  Call this BEFORE issuing the speak command: the listener is in place first,
 *  so a fast utterance cannot finish before we are watching. The returned
 *  function resolves when the utterance reports finished, cancelled, or
 *  errored, and tears the listener down either way.
 *
 *  The Windows speak command returns as soon as the request is queued, so
 *  without this the picker would clear its "playing" indicator before a word
 *  had been spoken. `use-tts` has an equivalent helper, but it is private to
 *  that module and only settles while the TTS hook is mounted — the picker runs
 *  during onboarding and in Settings, so it waits for itself.
 *
 *  The returned waiter carries a `cancel()` for the case where the speak
 *  command never runs, so the listener is torn down on that path too.
 */
type TerminalWaiter = (() => Promise<void>) & { cancel: () => Promise<void> };

async function armUtteranceTerminal(
  utteranceId: string,
): Promise<TerminalWaiter> {
  let settle: () => void = () => undefined;
  const reachedTerminal = new Promise<void>((resolve) => {
    settle = resolve;
  });
  const unlisten = await listen<{ utteranceId?: string; state?: string }>(
    "denker-agent-voice-state",
    (event) => {
      if (event.payload?.utteranceId !== utteranceId) return;
      if (event.payload?.state === "started") return;
      settle();
    },
  );
  const wait = async () => {
    const timer = window.setTimeout(settle, PREVIEW_TERMINAL_TIMEOUT_MS);
    try {
      await reachedTerminal;
    } finally {
      window.clearTimeout(timer);
      unlisten();
    }
  };
  return Object.assign(wait, {
    cancel: async () => {
      unlisten();
    },
  });
}

/** Speak the preview line with a specific Windows voice.
 *
 *  Separate from `previewDenkerAgentVoice` because the two take different
 *  things: that one takes a Denker slot id, this one takes an installed Windows
 *  voice. Resolves when the preview has finished (or been superseded), so the
 *  caller's "playing" indicator tracks the audio. Returns false if a newer
 *  preview superseded this one. */
export async function previewWindowsSystemVoice(
  voiceId: string,
): Promise<boolean> {
  if (!isTauri() || typeof window === "undefined") return false;
  const requestSeq = claimPreviewRequest();
  await cancelAllVoiceEngines();
  if (!isCurrentPreviewRequest(requestSeq)) return false;

  const utteranceId = `preview-${requestSeq}`;
  const waitForTerminal = await armUtteranceTerminal(utteranceId);
  try {
    await invoke("denker_system_voice_speak", {
      text: voicePreviewLine(),
      voiceId,
      utteranceId,
    });
  } catch (error) {
    // The listener is armed before the command is issued, so a rejected
    // command has to tear it down here — otherwise every failed preview
    // leaves a permanent listener behind.
    await waitForTerminal.cancel();
    throw error;
  }
  await waitForTerminal();
  return isCurrentPreviewRequest(requestSeq);
}

/** Stop speech on BOTH engines.
 *
 *  The voice picker lets a Windows user audition one engine and then the other,
 *  so cancelling only the currently-selected engine would leave the one they
 *  just switched away from still talking over the new sample. */
export function cancelAllVoiceEngines(): Promise<void> {
  return Promise.all([
    invoke<void>("denker_system_voice_cancel").catch(() => undefined),
    invoke<void>("denker_audio_cancel", { reason: "explicit" }).catch(
      () => undefined,
    ),
  ]).then(() => undefined);
}
